import React from 'react';
import { ResponsiveBar } from '@nivo/bar';
import {
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  useTheme
} from '@mui/material';
import { 
  formatCurrency, 
  formatYAxisValue, 
  formatHorizontalBarLabel, 
  getHorizontalBarChartConfig 
} from './formatters';

interface BasePartnerData {
  nome: string;
  cnpj_cpf: string;
  documentos: number;
  produtos_distintos: number;
  participacao_percentual: number;
}

interface DrillDownTopPartnersChartProps<T extends BasePartnerData> {
  data: T[];
  valueKey: keyof T;
  nameKey: keyof T;
  indexKey: string;
  barKey: string;
  color: string;
  title: string;
  maxItems?: number;
  height?: number;
  gridSize?: {
    xs?: number;
    md?: number;
  };
}

const DrillDownTopPartnersChart = <T extends BasePartnerData>({
  data,
  valueKey,
  nameKey,
  indexKey,
  barKey,
  color,
  title,
  height = 400,
  gridSize = { xs: 12, md: 12 }
}: DrillDownTopPartnersChartProps<T>) => {
  const theme = useTheme();

  if (!data || data.length === 0) {
    return null;
  }

  // Preparar dados para o gráfico
  const chartData = data
    .sort((a, b) => (a[valueKey] as number) - (b[valueKey] as number)) // Ordenar por valor em ordem crescente
    .map(item => {
      const name = item[nameKey] as string;
      const value = item[valueKey] as number;
      
      return {
        [indexKey]: formatHorizontalBarLabel(name, 30),
        [barKey]: value,
        nomeCompleto: name, // Nome completo para tooltip
        participacao: item.participacao_percentual,
        documentos: item.documentos,
        produtos_distintos: item.produtos_distintos
      };
    });

  const chartConfig = getHorizontalBarChartConfig(chartData.length, true);

  return (
    <Grid item xs={gridSize.xs} md={gridSize.md}>
      <Card sx={{ overflow: 'visible' }}>
        <CardContent sx={{ overflow: 'visible' }}>
          <Typography variant="h6" gutterBottom>
            {title}
          </Typography>
          <Box sx={{ 
            height: chartConfig.height,
            overflow: 'visible', // Permite tooltip fora dos limites
            position: 'relative',
            zIndex: 1 // Garante que tooltips fiquem acima
          }}>
            <ResponsiveBar
              data={chartData}
              keys={[barKey]}
              indexBy={indexKey}
              margin={chartConfig.margin}
              padding={chartConfig.padding}
              innerPadding={chartConfig.innerPadding}
              layout="horizontal"
              colors={[color]}
              axisTop={null}
              axisRight={null}
              axisBottom={{
                ...chartConfig.axisBottom,
                legend: 'Valor (R$)'
              }}
              axisLeft={chartConfig.axisLeft}
              labelSkipWidth={12}
              labelSkipHeight={12}
              labelTextColor="#fff"
              valueFormat={formatCurrency}
              theme={{
                axis: {
                  ticks: {
                    text: {
                      fontSize: 12
                    }
                  }
                }
              }}
              tooltip={({ id, value, data }) => (
                <Box sx={{ 
                  p: 2, 
                  bgcolor: 'background.paper', 
                  borderRadius: 2, 
                  boxShadow: 3,
                  minWidth: 280,
                  maxWidth: 400,
                  border: `1px solid ${theme.palette.divider}`
                }}>
                  <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                    👤 {(data as any).nomeCompleto || id}
                  </Typography>
                  <Typography variant="h6" sx={{ color, mb: 1 }}>
                    {formatCurrency(value as number)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block">
                    📊 {(data as any).participacao?.toFixed(1)}% do total
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block">
                    📄 {(data as any).documentos} documentos • 📦 {(data as any).produtos_distintos} produtos
                  </Typography>
                </Box>
              )}
            />
          </Box>
        </CardContent>
      </Card>
    </Grid>
  );
};

export default DrillDownTopPartnersChart; 